import { getProjectCurrencySign } from '@stokr/components-library'
import { ProjectStatus } from 'constants/enums'
import type { GeneralContent } from 'models/projectContent'
import type { Project } from 'models/project'

export interface RedemptionCompletedView {
  symbol: string
  /** What was paid out to investors (General tab), formatted; absent until back-office sets it. */
  payout?: string
}

/** Back-office closed the product: every note is redeemed, nothing is left to invest, trade or redeem. */
export const isRedemptionCompleted = (project: Pick<Project, 'status'> | undefined): boolean =>
  project?.status === ProjectStatus.COMPLETED

/** With its sign (Figma "€ 80,000,000", "₿ 1,212.23"); a currency without one with its code. */
export const formatPayout = (value: number, currency: string): string => {
  const prefix = getProjectCurrencySign(currency) || currency
  const number = value.toLocaleString('en-US', { maximumFractionDigits: 8 })
  return prefix ? `${prefix} ${number}` : number
}

/**
 * The card's figures. The payout comes from the General tab, not the project's `settlementValue`:
 * that is what was issued at closing, not what investors were paid. Moves to the project record
 * once the backend has a field for it.
 */
export const toRedemptionCompleted = (
  project: Pick<Project, 'status' | 'tokenSymbol'> | undefined,
  general: Pick<GeneralContent, 'totalPayout' | 'totalPayoutCurrency'> | undefined,
): RedemptionCompletedView | null => {
  if (!project || !isRedemptionCompleted(project)) return null
  const raw = String(general?.totalPayout ?? '').trim()
  const value = Number(raw)
  return {
    symbol: project.tokenSymbol?.trim() ?? '',
    payout: raw && Number.isFinite(value) ? formatPayout(value, general?.totalPayoutCurrency?.trim() ?? '') : undefined,
  }
}
